import { Col, message } from "antd";
import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import qs from "qs";
import { selectSelectedClientIds } from "redux/slices/globalSelectorSlice";
import { usePageFilter } from "hooks/usePageFilters";
import { DataTableLayout } from "common/components/dataTableLayout/dataTableLayout";
import { selectUserId, selectUserRoles } from "redux/slices/userSlice";
import { isPermissionPresent } from "utilities/generalUtility";
import { ExportDataButton } from "common/components/ExportDataButton";
import K from "../../../utilities/constants";
import { Columns } from "./columns";
import { RecruitersSelect } from "./recruitersSelect";

const pagePrefix = "engagements";

export const EngagementsContainer = ({
  getData,
  tableConfigKey,
  onExport,
  renderTable,
}) => {
  const history = useHistory();

  const clients = useSelector(selectSelectedClientIds);
  const userId = useSelector(selectUserId);
  const roles = useSelector(selectUserRoles);

  const gridRef = useRef(null);

  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = usePageFilter(`${pagePrefix}_search`);
  const [recruiters, setRecruiters] = usePageFilter(
    `${pagePrefix}_recruiters`,
    [],
  );

  const recruiterIds = useMemo(() => recruiters.map((r) => r.id), [recruiters]);

  const isExportDisabled = totalCount === 0;

  const hasAccessToCandidateProfile = isPermissionPresent(
    K.Permissions.ViewCandidateJobs,
    roles,
  );

  const hasAccessToRecruiters = isPermissionPresent(
    K.Permissions.ManageEngagementsForAssignedClients,
    roles,
  );

  const onExportRequested = async () => {
    try {
      const { columnApi } = gridRef.current;
      const sortColumn = columnApi.getColumnState().find((c) => c.sort);

      const params = {
        client: clients,
        sortBy: sortColumn?.colId,
        sortOrder: sortColumn?.sort,
        search: search ?? undefined,
        recruiter: recruiterIds,
      };

      await onExport(params);
      message.success(
        "Engagement export request successfully received! Check your email shortly",
      );
    } catch (err) {
      console.error(err);
    }
  };

  const onRecruitersChange = (ids, selectedRecruiters) => {
    setRecruiters(selectedRecruiters);
  };

  const onRowClicked = useCallback(
    ({ data }) => {
      if (!hasAccessToCandidateProfile) {
        return;
      }

      const { candidateId, candidateJobEntryId } = data;

      const params = {
        tab: "jobs",
        jobEntry: candidateJobEntryId,
        candidateTab: "engagements",
      };

      const queryString = qs.stringify(params);

      history.push(`/candidates/detail/${candidateId}?${queryString}`);
    },
    [hasAccessToCandidateProfile, history],
  );

  const getServerSideData = useCallback(
    (options) => {
      return getData({
        ...options,
        client: clients,
        search: search ?? undefined,
        recruiter: recruiterIds,
      });
    },
    [getData, clients, search, recruiterIds],
  );

  const onDataFetchSuccess = useCallback(
    (data) => {
      setTotalCount(data.rowCount);
    },
    [setTotalCount],
  );

  return (
    <DataTableLayout
      searchDefaultValue={search}
      onSearchChange={setSearch}
      renderFilters={() => (
        <>
          {hasAccessToRecruiters && (
            <Col style={{ width: 200 }}>
              <RecruitersSelect
                defaultValue={recruiters}
                onChange={onRecruitersChange}
                clients={clients}
              />
            </Col>
          )}
        </>
      )}
      actions={[
        {
          renderer: () => (
            <Col>
              <ExportDataButton
                disabled={isExportDisabled}
                onClick={onExportRequested}
              />
            </Col>
          ),
        },
      ]}
    >
      {renderTable({
        ref: gridRef,
        userId,
        configKey: tableConfigKey,
        getData: getServerSideData,
        defaultColumnConfig: Columns,
        onDataFetchSuccess,
        onRowClicked,
        rowStyle: {
          cursor: hasAccessToCandidateProfile ? "pointer" : "default",
        },
      })}
    </DataTableLayout>
  );
};
